import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import {
  PaperAirplaneIcon,
  LockClosedIcon,
  PaperClipIcon,
  XMarkIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import supportChatService, {
  type SupportMessage,
  type SupportMessageAttachment,
  type SupportThread,
} from '@/services/supportChatService'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { formatDate } from '@/lib/utils'
import toast from 'react-hot-toast'
import { useSupportUnreadStore } from '@/stores/supportUnreadStore'
import { useAuthStore } from '@/stores/authStore'
import { useTranslation } from '@/i18n/useTranslation'

const MAX_FILES = 5
const MAX_FILE_BYTES = 10 * 1024 * 1024
const ACCEPT =
  'image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

type SupportTransport = {
  listMessages: (
    threadId: number,
    params?: { after_message_id?: number; limit?: number },
  ) => Promise<{ success: boolean; data?: SupportMessage[] }>
  postMessage: (
    threadId: number,
    body: { body: string; is_internal?: boolean; files?: File[] },
  ) => Promise<{ success: boolean; data?: SupportMessage }>
  markRead: (threadId: number, lastReadMessageId?: number) => Promise<{ success: boolean }>
  getUnread?: (shopId: number) => Promise<{
    success: boolean
    data?: { unread_threads: number; unread_messages: number }
  }>
  openEventStream?: (opts: {
    shopId?: number
    threadId?: number
    onEvent: (event: import('@/services/supportChatService').SupportChatEvent) => void
  }) => () => void
}

type Props = {
  thread: SupportThread
  mode: 'staff' | 'customer'
  dateFormat?: string
  onThreadUpdated?: (thread: SupportThread) => void
  /** Fired after this thread has been marked read (list + sidebar badges should clear). */
  onMarkedRead?: (threadId: number) => void
  /** Override API (e.g. platform support). Defaults to shop support chat. */
  transport?: SupportTransport
  /** Skip updating the global shop support unread store. */
  skipGlobalUnreadBadge?: boolean
  /**
   * When false, rely on polling (and parent SSE) instead of a per-thread EventSource.
   * Avoids exhausting the browser's per-host connection limit when the inbox already
   * holds a shop-level stream.
   */
  enableEventStream?: boolean
  /** Bump from parent (e.g. on shop SSE message.created) to pull new messages sooner. */
  refreshNonce?: number
}

function serveUrl(filePath: string): string {
  const normalized = filePath.replace(/^\/?uploads\//, '').replace(/^\//, '')
  return `/api/uploads/serve/${normalized.split('/').map(encodeURIComponent).join('/')}`
}

function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function MessageAttachments({
  attachments,
  mine,
}: {
  attachments: SupportMessageAttachment[]
  mine: boolean
}) {
  if (!attachments.length) return null
  return (
    <div className="mt-2 space-y-2">
      {attachments.map((att) => {
        const url = serveUrl(att.file_path)
        const isImage = att.mime_type.startsWith('image/')
        if (isImage) {
          return (
            <a
              key={att.attachment_id}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="block overflow-hidden rounded-lg border border-black/10"
            >
              <img
                src={url}
                alt={att.file_name}
                className="max-h-56 max-w-full object-contain bg-black/5"
              />
            </a>
          )
        }
        return (
          <a
            key={att.attachment_id}
            href={url}
            target="_blank"
            rel="noreferrer"
            className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs ${
              mine
                ? 'bg-white/10 hover:bg-white/15'
                : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <DocumentIcon className="h-4 w-4 shrink-0 opacity-80" />
            <span className="min-w-0 flex-1 truncate font-medium">{att.file_name}</span>
            <span className="shrink-0 opacity-70">{formatBytes(att.file_size)}</span>
            <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0 opacity-70" />
          </a>
        )
      })}
    </div>
  )
}

export function SupportConversationPanel({
  thread,
  mode,
  dateFormat = 'DD/MM/YYYY',
  onThreadUpdated,
  onMarkedRead,
  transport,
  skipGlobalUnreadBadge = false,
  enableEventStream = true,
  refreshNonce = 0,
}: Props) {
  const { t } = useTranslation('common')
  const shopId = useAuthStore((s) => s.user?.shop_id)
  const setUnread = useSupportUnreadStore((s) => s.setUnread)
  const [messages, setMessages] = useState<SupportMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [body, setBody] = useState('')
  const [internalNote, setInternalNote] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const threadId = thread.thread_id
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  const onMarkedReadRef = useRef(onMarkedRead)
  onMarkedReadRef.current = onMarkedRead
  const onThreadUpdatedRef = useRef(onThreadUpdated)
  onThreadUpdatedRef.current = onThreadUpdated
  const defaultTransport = useMemo<SupportTransport>(
    () => ({
      listMessages: supportChatService.listMessages,
      postMessage: supportChatService.postMessage,
      markRead: supportChatService.markRead,
      getUnread: supportChatService.getUnread,
      openEventStream: supportChatService.openEventStream,
    }),
    [],
  )
  const api = transport ?? defaultTransport

  const refreshUnreadBadge = async () => {
    if (skipGlobalUnreadBadge || !shopId || !api.getUnread) return
    try {
      const res = await api.getUnread(shopId)
      if (res.success && res.data) {
        setUnread(res.data.unread_threads, res.data.unread_messages)
      }
    } catch {
      /* ignore */
    }
  }

  const markThreadRead = async (messageId?: number) => {
    onMarkedReadRef.current?.(threadId)
    try {
      await api.markRead(threadId, messageId)
    } catch {
      /* non-blocking */
    }
    await refreshUnreadBadge()
  }

  const mergeMessages = (incoming: SupportMessage[]) => {
    if (!incoming.length) return
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.message_id))
      const next = incoming.filter((m) => {
        if (seen.has(m.message_id)) return false
        if (mode === 'customer' && m.is_internal) return false
        return true
      })
      return next.length ? [...prev, ...next] : prev
    })
  }

  const pullNewerMessages = async () => {
    try {
      const after = messagesRef.current[messagesRef.current.length - 1]?.message_id
      const res = await api.listMessages(threadId, after ? { after_message_id: after } : undefined)
      if (res.success && res.data?.length) {
        mergeMessages(res.data)
        const last = res.data[res.data.length - 1]
        if (last) void markThreadRead(last.message_id)
      }
    } catch {
      /* ignore background poll errors */
    }
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setMessages([])
    setPendingFiles([])

    ;(async () => {
      try {
        const res = await api.listMessages(threadId)
        if (cancelled) return
        if (res.success && res.data) {
          setMessages(res.data)
          setLoading(false)
          const last = res.data[res.data.length - 1]
          // Mark-read must not block painting the conversation (can hang under connection pressure).
          if (last) {
            void markThreadRead(last.message_id)
          } else {
            onMarkedReadRef.current?.(threadId)
            void refreshUnreadBadge()
          }
        } else if (!cancelled) {
          setLoading(false)
        }
      } catch {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (!enableEventStream || !api.openEventStream) return
    return api.openEventStream({
      threadId,
      onEvent: (event) => {
        if (event.type === 'message.created') {
          const payload = event.payload as { message?: SupportMessage } | undefined
          const msg = payload?.message
          if (!msg || msg.thread_id !== threadId) return
          if (mode === 'customer' && msg.is_internal) return
          setMessages((prev) => {
            if (prev.some((m) => m.message_id === msg.message_id)) return prev
            return [...prev, msg]
          })
          void markThreadRead(msg.message_id)
        }
        if (event.type === 'thread.updated') {
          const payload = event.payload as { thread?: SupportThread } | undefined
          if (payload?.thread) onThreadUpdatedRef.current?.(payload.thread)
        }
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, mode, enableEventStream, api])

  // Polling fallback (and primary live path when enableEventStream is false)
  useEffect(() => {
    const id = window.setInterval(() => {
      void pullNewerMessages()
    }, 15000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, api])

  useEffect(() => {
    if (!refreshNonce) return
    void pullNewerMessages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce])

  const addFiles = (list: FileList | File[]) => {
    const incoming = Array.from(list)
    const next: File[] = [...pendingFiles]
    for (const file of incoming) {
      if (next.length >= MAX_FILES) {
        toast.error(t('platformSupport.maxFiles', { max: MAX_FILES }))
        break
      }
      if (file.size > MAX_FILE_BYTES) {
        toast.error(t('platformSupport.fileTooLarge', { name: file.name }))
        continue
      }
      if (next.some((f) => f.name === file.name && f.size === file.size)) continue
      next.push(file)
    }
    setPendingFiles(next)
  }

  const handleSend = async (e: FormEvent) => {
    e.preventDefault()
    const text = body.trim()
    if ((!text && pendingFiles.length === 0) || sending) return
    setSending(true)
    try {
      const res = await api.postMessage(threadId, {
        body: text,
        is_internal: mode === 'staff' && internalNote,
        files: pendingFiles.length ? pendingFiles : undefined,
      })
      if (res.success && res.data) {
        setMessages((prev) => {
          if (prev.some((m) => m.message_id === res.data!.message_id)) return prev
          return [...prev, res.data!]
        })
        setBody('')
        setPendingFiles([])
        setInternalNote(false)
      }
    } catch {
      toast.error(t('platformSupport.sendFailed'))
    } finally {
      setSending(false)
    }
  }

  const isMine = (m: SupportMessage) => {
    if (mode === 'staff') return m.sender_type === 'staff'
    return m.sender_type === 'customer'
  }

  const canSend = Boolean(body.trim() || pendingFiles.length) && !sending

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">{t('platformSupport.noMessages')}</p>
        ) : (
          messages.map((m) => {
            const mine = isMine(m)
            return (
              <div
                key={m.message_id}
                className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                    m.is_internal
                      ? 'border border-amber-200 bg-amber-50 text-amber-950'
                      : mine
                        ? 'bg-slate-800 text-white'
                        : 'bg-white border border-gray-200 text-gray-900'
                  }`}
                >
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] opacity-80">
                    <span className="font-medium">
                      {m.is_internal
                        ? t('platformSupport.internalNoteLabel')
                        : m.sender_name || m.sender_type}
                    </span>
                    {m.is_internal && <LockClosedIcon className="h-3 w-3" />}
                    <span>{formatDate(m.created_at, dateFormat)}</span>
                  </div>
                  {m.body ? (
                    <p className="whitespace-pre-wrap break-words leading-relaxed">{m.body}</p>
                  ) : null}
                  <MessageAttachments attachments={m.attachments || []} mine={mine} />
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={handleSend}
        className="border-t border-gray-200 bg-white px-3 py-3"
      >
        {mode === 'staff' && (
          <label className="mb-2 flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={internalNote}
              onChange={(e) => setInternalNote(e.target.checked)}
              className="rounded border-gray-300"
            />
            {t('platformSupport.internalNote')}
          </label>
        )}
        {pendingFiles.length > 0 && (
          <ul className="mb-2 flex flex-wrap gap-2">
            {pendingFiles.map((file, idx) => (
              <li
                key={`${file.name}-${file.size}-${idx}`}
                className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700"
              >
                <DocumentIcon className="h-4 w-4 shrink-0 text-gray-500" />
                <span className="truncate">{file.name}</span>
                <button
                  type="button"
                  className="rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                  aria-label={`Remove ${file.name}`}
                  onClick={() =>
                    setPendingFiles((prev) => prev.filter((_, i) => i !== idx))
                  }
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files)
              e.target.value = ''
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || pendingFiles.length >= MAX_FILES}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            aria-label={t('platformSupport.attachFiles')}
            title={t('platformSupport.attachFiles')}
          >
            <PaperClipIcon className="h-5 w-5" />
          </button>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            placeholder={
              thread.status === 'resolved' && mode === 'customer'
                ? t('platformSupport.replyToReopen')
                : t('platformSupport.typeMessage')
            }
            className="min-h-[44px] flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            onPaste={(e) => {
              const items = e.clipboardData?.files
              if (items?.length) {
                addFiles(items)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void handleSend(e as unknown as FormEvent)
              }
            }}
          />
          <button
            type="submit"
            disabled={!canSend}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
            aria-label={t('platformSupport.send')}
          >
            {sending ? <LoadingSpinner size="sm" /> : <PaperAirplaneIcon className="h-5 w-5" />}
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-gray-400">
          {t('platformSupport.attachHint', { max: MAX_FILES })}
        </p>
      </form>
    </div>
  )
}
